/**
 * Pure decision for the site calendar realtime handler.
 *
 * Applies the 'calendar' contract's two guards — a pre-seed no-op (drop emits
 * before the view has fetched an occurrence window) and a monotonic generated_at
 * check (drop an emit older than the last applied) — and, when an emit passes,
 * returns exactly what the handler must act on: the series to merge and the
 * window to refetch. Kept import-free so it is unit-testable without the socket
 * or redux machinery.
 *
 * Contains:
 * - resolveCalendarEmit — (payload, meta, calendarState) -> apply plan | null
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * @param {?{ events?: object[] }} payload - the 'calendar' contract data.
 * @param {?{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata.
 * @param {{ fetchedWindow: {start: ?string, end: ?string}, lastGeneratedAt: ?string }} calendar
 *   - the calendar slice state.
 * @returns {?{ siteId: (number|string), events: object[], generatedAt: ?string,
 *   window: {start: string, end: string} }} The apply plan, or null to drop.
 */
export function resolveCalendarEmit(payload, meta, calendar) {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) {
    return null;
  }

  const { fetchedWindow, lastGeneratedAt } = calendar;
  if (!fetchedWindow || fetchedWindow.start == null) {
    return null;
  }

  const incomingGeneratedAt = meta?.generatedAt ?? null;
  if (
    incomingGeneratedAt != null &&
    lastGeneratedAt != null &&
    new Date(incomingGeneratedAt) < new Date(lastGeneratedAt)
  ) {
    return null;
  }

  return {
    siteId,
    events: payload.events ?? [],
    generatedAt: incomingGeneratedAt,
    window: { start: fetchedWindow.start, end: fetchedWindow.end },
  };
}
